import { Link } from "react-router-dom";
import { ArrowRight, User, Building2, Home, Rocket, Check } from "lucide-react";
import { PublicNav, PublicFooter, PublicHero } from "@/components/public/PublicChrome";
import { useAuth } from "@/context/AuthContext";

const SOLUTIONS = [
  {
    icon: User, key: "individuals", accent: "#1a5fd0",
    audience: "For Individuals",
    title: "Rebuild and grow your personal credit",
    body: "Whether you're recovering, building from a thin file, or optimizing a good score, get a clear plan and the tools to execute it.",
    outcomes: ["Raise your FICO with a prioritized action plan", "Report rent, utilities and bills to build history", "Simulate changes before you make them"],
    products: [["Personal Credit", "/personal-credit"], ["Credit Builder", "/credit-builder"]],
  },
  {
    icon: Building2, key: "businesses", accent: "#b0862f",
    audience: "For Small Businesses",
    title: "Build a fundable business — the right way",
    body: "Separate personal and business liability, build a real business credit file, and get funding-ready before you ever apply.",
    outcomes: ["Establish business credit across all three bureaus", "Open reporting tradelines and grow your PAYDEX", "Prep funding applications with your AI Capital Crew"],
    products: [["Business Credit", "/business-credit"], ["Funding Readiness", "/funding-readiness"], ["Marketplace", "/marketplace"]],
  },
  {
    icon: Home, key: "investors", accent: "#059669",
    audience: "For Real Estate Investors",
    title: "Turn credit strength into property",
    body: "Convert improved credit and funding readiness into acquisition power for income-producing residential and commercial assets.",
    outcomes: ["Understand what real-estate lenders look for", "Track readiness toward acquisition financing", "Leverage business credit to protect personal capacity"],
    products: [["Funding Marketplace", "/marketplace"], ["Capital Passport", "/capital-passport"]],
  },
  {
    icon: Rocket, key: "startups", accent: "#1a5fd0",
    audience: "For Startups & Founders",
    title: "Set the financial foundation from day one",
    body: "Incorporate cleanly, establish identity with the bureaus, and build the credit and cash-flow profile investors and lenders expect.",
    outcomes: ["Business identity: EIN, DUNS, clean records", "Non-dilutive capital paths and grants", "Cash-flow tools that keep your books fundable"],
    products: [["Business Identity", "/business-identity"], ["Opportunities", "/opportunities"], ["Cash Flow", "/cash-flow"]],
  },
];

function SolutionsMotif() {
  const nodes = [[24, 28], [24, 132], [176, 28], [176, 132]];
  return (
    <div className="relative hidden h-56 w-full items-center justify-center lg:flex" aria-hidden>
      <div className="absolute h-44 w-44 rounded-full bg-[#1a5fd0]/10 blur-2xl animate-float" />
      <svg viewBox="0 0 200 160" className="relative h-full w-full max-w-sm">
        {nodes.map(([x, y], i) => (
          <g key={i}>
            <line x1={x} y1={y} x2="100" y2="80" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 5" />
            <circle cx={x} cy={y} r="8" fill="#fff" stroke={i % 2 ? "#b0862f" : "#1a5fd0"} strokeWidth="2.5" />
          </g>
        ))}
        <circle cx="100" cy="80" r="18" fill="#0f172a" />
        <circle cx="100" cy="80" r="18" fill="none" stroke="#d8b25a" strokeWidth="2.5" className="animate-pulse" />
        <circle cx="100" cy="80" r="3.5" fill="#d8b25a" />
      </svg>
    </div>
  );
}

export default function Solutions() {
  const { user } = useAuth();
  const primaryTo = user ? "/adaptive" : "/register";
  const goTo = (to) => (user ? to : "/register");
  const link = goTo;
  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="solutions-page">
      <PublicNav />
      <PublicHero
        eyebrow="Solutions"
        title="Built for where you are,"
        accent="and where you're going."
        sub="The same platform adapts to your goal — repairing personal credit, building a fundable business, acquiring real estate, or launching a startup."
        visual={<SolutionsMotif />}
      />
      <div className="mx-auto max-w-7xl px-5 pb-16 pt-10 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {SOLUTIONS.map((s) => (
            <div key={s.key} data-testid={`solution-card-${s.key}`} className="card-spotlight hover-lift flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all hover:border-slate-300">           
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `${s.accent}15` }}>
                  <s.icon size={22} style={{ color: s.accent }} />
                </span>
                <div className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#a37d2a]">{s.audience}</div>
              </div>
              <h3 className="mt-4 font-serif-display text-2xl font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-500">{s.body}</p>
              <ul className="mt-4 space-y-2">
                {s.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-2.5 text-[13px] text-slate-700">
                    <Check size={16} className="mt-0.5 shrink-0" style={{ color: s.accent }} />{o}
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
                {s.products.map(([label, to]) => (
                  <Link key={label} to={link(to)} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900">{label}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-slate-200/80 bg-white p-8 text-center" data-testid="solutions-cta">
          <div className="font-serif-display text-2xl font-medium text-slate-900">Not sure which path is yours?</div>
          <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-500">Create a free account and the Adaptive OS will assemble the right plan around your goal in minutes.</p>
          <Link to={primaryTo} data-testid="solutions-cta-start" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#0f172a] px-6 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-[#1a5fd0]">Start free <ArrowRight size={15} /></Link>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
